import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, IconButton, LinearProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { AddCircle, Map1 } from 'iconsax-react';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CreateRouteDialog from './CreateRouteDialog';
import { MoreHoriz } from '@mui/icons-material';
import { StyledMenu } from '@ui/components/common/Menu';
import { Iconly } from 'react-iconly';
import LocationsListDialog from './LocationsListDialog';
import VehiclesListDialog from './VehiclesListDialog';
import RouteMapDialog from './RouteMapDialog';
import AppAlert from '@ui/components/common/Alert';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import { Icon } from '@iconify/react';
import MetricChip from '@ui/components/common/MetricChip';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { getPickupLocationsNotPaginatedByEventIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { getEventVehiclesNotPaginatedByEventIdAsync } from '@application/features/eventVehicle/eventVehicleThunk';
import { getRoutesByEventIdAsync, getRouteByIdAsync, createRouteAsync, updateRouteAsync, deleteRouteAsync } from '@application/features/transportRoute/transportRouteThunk';
import { ExportSquare } from 'iconsax-react';
import { downloadFamilyFestRouteInformationExcelAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { CateringPackageEnumDisplay } from '@application/enums/catering-package-enum';
const RoutesContent = ({ onGoToLocationsTab }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { routes } = useAppSelector((state) => state.transportRoute);
    const { pickupLocationsList } = useAppSelector((state) => state.pickupLocation);
    const { vehiclesNotPaginated: vehicles } = useAppSelector((state) => state.eventVehicle);
    const currentUserRoles = useSelector((state) => state.auth.currentUser?.role);
    const isHappinessManager = Array.isArray(currentUserRoles) && currentUserRoles.includes('HappinessManager');
    const isSfeermeester = Array.isArray(currentUserRoles) && currentUserRoles.includes('Sfeermeester');
    // Routes, and the pickup-location/vehicle dropdown options, are scoped by the selected
    // companies via the auth token, so both must be refetched on a sidebar company change.
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const [searchValue, setSearchValue] = useState('');
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [editingRoute, setEditingRoute] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [openLocationsDialog, setOpenLocationsDialog] = useState(false);
    const [selectedLocationsForDialog, setSelectedLocationsForDialog] = useState([]);
    const [openVehiclesDialog, setOpenVehiclesDialog] = useState(false);
    const [selectedVehiclesForDialog, setSelectedVehiclesForDialog] = useState([]);
    const [selectedRouteNameForVehicles, setSelectedRouteNameForVehicles] = useState('');
    const [openMapDialog, setOpenMapDialog] = useState(false);
    const [selectedRouteNameForMap, setSelectedRouteNameForMap] = useState('');
    const [selectedPickupLocationsForMap, setSelectedPickupLocationsForMap] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [routeToDelete, setRouteToDelete] = useState(null);
    const [prepopulatedVehicles, setPrepopulatedVehicles] = useState([]);
    const [editingRouteDetail, setEditingRouteDetail] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [filters, setFilters] = useState({});
    const [gridFilterOpen, setGridFilterOpen] = useState(false);
    const [resetFiltersKey, setResetFiltersKey] = useState(0);
    const [sortModel, setSortModel] = useState([]);
    useEffect(() => {
        if (!eventId)
            return;
        const sort = sortModel[0];
        dispatch(getRoutesByEventIdAsync({
            eventId,
            query: {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: searchValue || undefined,
                sortBy: sort?.field,
                sortOrder: sort?.sort ?? 'desc',
                ...filters
            }
        }));
    }, [dispatch, eventId, paginationModel, searchValue, filters, sortModel, selectedCompanyIds]);
    useEffect(() => {
        if (!eventId)
            return;
        dispatch(getPickupLocationsNotPaginatedByEventIdAsync({ eventId, includeClosed: false }));
        dispatch(getEventVehiclesNotPaginatedByEventIdAsync({ eventId }));
    }, [dispatch, eventId, selectedCompanyIds]);
    const pickupLocationOptions = useMemo(() => (pickupLocationsList ?? []).map((pl) => ({
        value: pl.id,
        label: pl.name,
        location: pl.address,
        latitude: pl.latitude,
        longitude: pl.longitude
    })), [pickupLocationsList]);
    // Route vehicle dropdown option: vehicle name, with vehicle number, subcontractor and capacity (each with an icon) underneath
    const toVehicleOption = useCallback((v) => {
        const meta = [
            { icon: 'hugeicons:car-01', text: v.vehicleName ? v.vehicleNumber ?? '' : '' },
            { icon: 'hugeicons:building-02', text: v.subcontractorName ?? '' },
            {
                icon: 'hugeicons:chair-01',
                text: t('events.eventDetails.transportTab.vehicleCapacity', { count: v.totalCapacity })
            }
        ].filter((item) => item.text);
        return {
            value: v.id,
            label: v.vehicleName || v.vehicleNumber || '',
            // plain text kept for search; meta is what gets rendered
            name: meta.map((item) => item.text).join(' '),
            meta
        };
    }, [t]);
    const vehicleOptions = useMemo(() => vehicles.map(toVehicleOption), [vehicles, toVehicleOption]);
    // Route dialog only offers vehicles not linked to another route (the route being edited keeps its own)
    const availableVehicleOptions = useMemo(() => vehicles
        .filter((v) => !v.routeId || v.routeId === editingRoute?.id)
        .map(toVehicleOption), [vehicles, editingRoute?.id, toVehicleOption]);
    const hasEnoughPickupLocations = pickupLocationOptions.length >= 2;
    const routeRows = useMemo(() => (routes?.items ?? []).map((r) => ({
        ...r,
        pickupLocations: [],
        routeVehicles: []
    })), [routes]);
    const filteredRoutes = useMemo(() => routeRows, [routeRows]);
    const handleOpenRowMenu = (event, route) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedRoute(route);
    };
    const handleCloseRowMenu = () => {
        setMenuAnchorEl(null);
        setSelectedRoute(null);
    };
    const handleEditRoute = useCallback(async () => {
        if (!selectedRoute)
            return;
        handleCloseRowMenu();
        try {
            const detail = await dispatch(getRouteByIdAsync(selectedRoute.id)).unwrap();
            const prepopulatedPickupLocations = detail.stops.map((stop) => {
                const plOption = pickupLocationOptions.find((pl) => pl.value === stop.pickupLocationId);
                return {
                    value: stop.pickupLocationId,
                    label: stop.name,
                    location: stop.address,
                    latitude: plOption?.latitude,
                    longitude: plOption?.longitude
                };
            });
            const prepopulatedVehicles = detail.vehicles.map((rv) => toVehicleOption({ ...rv, id: rv.vehicleId }));
            setEditingRouteDetail(detail);
            setEditingRoute({
                ...selectedRoute,
                pickupLocations: prepopulatedPickupLocations,
                routeVehicles: []
            });
            setOpenCreateDialog(true);
            setPrepopulatedVehicles(prepopulatedVehicles);
        }
        catch {
            toast.error(t('events.eventDetails.routeTab.routeUpdateFailed'));
        }
    }, [selectedRoute, dispatch, pickupLocationOptions, toVehicleOption, t]);
    const handleDeleteRoute = useCallback(() => {
        if (!selectedRoute)
            return;
        setRouteToDelete(selectedRoute);
        setDeleteConfirmOpen(true);
        handleCloseRowMenu();
    }, [selectedRoute]);
    const handleOpenDetailDialog = useCallback(async (row, type) => {
        try {
            const detail = await dispatch(getRouteByIdAsync(row.id)).unwrap();
            if (type === 'locations' || type === 'map') {
                const stops = detail.stops.map((stop) => {
                    const plOption = pickupLocationOptions.find((pl) => pl.value === stop.pickupLocationId);
                    return {
                        value: stop.pickupLocationId,
                        label: stop.name,
                        location: stop.address,
                        latitude: plOption?.latitude,
                        longitude: plOption?.longitude,
                        boardingCount: stop.boardingCount
                    };
                });
                if (type === 'locations') {
                    setSelectedLocationsForDialog(stops);
                    setOpenLocationsDialog(true);
                }
                else {
                    setSelectedRouteNameForMap(row.name);
                    setSelectedPickupLocationsForMap(stops);
                    setOpenMapDialog(true);
                }
            }
            else {
                const vehicleRows = detail.vehicles.map((rv) => {
                    const eventVehicle = vehicles.find((v) => v.id === rv.vehicleId);
                    return {
                        id: rv.id,
                        vehicleNumber: rv.vehicleNumber,
                        carrier: eventVehicle?.carrierName ?? '—',
                        driverNumber: eventVehicle?.driverNumber ?? '—',
                        seats: `${rv.assignedCount} / ${rv.totalCapacity}`
                    };
                });
                setSelectedRouteNameForVehicles(row.name);
                setSelectedVehiclesForDialog(vehicleRows);
                setOpenVehiclesDialog(true);
            }
        }
        catch {
            // axios interceptor handles token refresh and error reporting
        }
    }, [dispatch, pickupLocationOptions, vehicles, t]);
    const handleConfirmDelete = async () => {
        if (!routeToDelete)
            return;
        try {
            await dispatch(deleteRouteAsync(routeToDelete.id)).unwrap();
            toast.success(t('events.eventDetails.routeTab.routeDeletedSuccessfully'));
            if (eventId) {
                const sort = sortModel[0];
                dispatch(getRoutesByEventIdAsync({
                    eventId,
                    query: {
                        pageNumber: 1,
                        pageSize: paginationModel.pageSize,
                        searchString: searchValue || undefined,
                        sortBy: sort?.field,
                        sortOrder: sort?.sort ?? 'desc',
                        ...filters
                    }
                }));
                // vehicles of the deleted route become available again
                dispatch(getEventVehiclesNotPaginatedByEventIdAsync({ eventId }));
            }
        }
        catch (error) {
            toast.error(error?.error || t('events.eventDetails.routeTab.routeDeleteFailed'));
        }
        finally {
            setDeleteConfirmOpen(false);
            setRouteToDelete(null);
        }
    };
    const handleExportRoutes = async () => {
        if (!eventId)
            return;
        try {
            setIsExporting(true);
            await dispatch(downloadFamilyFestRouteInformationExcelAsync(eventId)).unwrap();
            toast.success(t('events.eventDetails.routeTab.exportSuccess', 'Routes exported successfully'));
        }
        catch (error) {
            toast.error(error?.message || t('events.eventDetails.routeTab.exportFailed', 'Export failed'));
        }
        finally {
            setIsExporting(false);
        }
    };
    const rowActions = useMemo(() => [
        {
            text: t('actions.edit'),
            icon: _jsx(Iconly, { name: 'Edit', set: 'curved', primaryColor: theme.palette.text.primary }),
            to: handleEditRoute,
            group: true
        },
        {
            text: t('actions.delete'),
            icon: _jsx(Iconly, { name: 'Delete', set: 'curved' }),
            color: 'error.light',
            to: handleDeleteRoute,
            group: true
        }
    ], [t, theme.palette.text.primary, handleEditRoute, handleDeleteRoute]);
    const columns = [
        {
            field: 'name',
            headerName: t('events.eventDetails.routeTab.routeName'),
            flex: 1.2,
            minWidth: 190,
            type: 'string',
            sortable: true,
            renderCell: (params) => (_jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: _jsx(Typography, { variant: 'body2', color: 'text.primary', title: params.row.name, sx: {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        width: '100%'
                    }, children: params.row.name }) }))
        },
        {
            field: 'locationCount',
            headerName: t('events.eventDetails.routeTab.locations'),
            flex: 0.8,
            minWidth: 110,
            type: 'number',
            align: 'center',
            headerAlign: 'center',
            sortable: true,
            renderCell: (params) => (_jsx(Box, { onClick: (event) => {
                    event.stopPropagation();
                    handleOpenDetailDialog(params.row, 'locations');
                }, sx: { cursor: 'pointer', display: 'inline-flex' }, children: _jsx(MetricChip, { value: params.row.locationCount }) }))
        },
        {
            field: 'startLocation',
            headerName: t('events.eventDetails.routeTab.startLocation'),
            flex: 1.3,
            minWidth: 220,
            renderCell: (params) => (_jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: _jsx(Typography, { variant: 'body2', color: 'text.primary', title: params.row.startLocation, sx: {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        width: '100%'
                    }, children: params.row.startLocation || '—' }) }))
        },
        {
            field: 'endLocation',
            headerName: t('events.eventDetails.routeTab.endLocation'),
            flex: 1.3,
            minWidth: 220,
            renderCell: (params) => (_jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: _jsx(Typography, { variant: 'body2', color: 'text.primary', title: params.row.endLocation, sx: {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        width: '100%'
                    }, children: params.row.endLocation || '—' }) }))
        },
        {
            field: 'vehicleCount',
            headerName: t('events.eventDetails.routeTab.vehicles'),
            flex: 0.7,
            minWidth: 90,
            sortable: true,
            renderCell: (params) => (_jsx(Box, { onClick: (event) => {
                    event.stopPropagation();
                    handleOpenDetailDialog(params.row, 'vehicles');
                }, sx: { cursor: 'pointer', display: 'inline-flex' }, children: _jsx(MetricChip, { value: params.row.vehicleCount }) }))
        },
        {
            field: 'totalSeats',
            headerName: t('events.eventDetails.routeTab.seats'),
            flex: 1,
            minWidth: 200,
            // Sum of vehicle capacities computed after pagination — not sortable without a query restructure.
            sortable: false,
            renderCell: (params) => {
                const usage = params.row.totalSeats > 0
                    ? Math.round((params.row.assignedCount / params.row.totalSeats) * 100)
                    : 0;
                return (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1.25, sx: { width: '100%', minWidth: 0 }, children: [_jsx(MetricChip, { value: params.row.assignedCount, total: params.row.totalSeats }), _jsx(Box, { sx: { flex: 1, minWidth: 44 }, children: _jsx(LinearProgress, { variant: 'determinate', value: Math.max(0, Math.min(100, usage)), sx: {
                                    height: '4px',
                                    borderRadius: '999px',
                                    bgcolor: (themeObj) => themeObj.palette.grey[300],
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: '999px',
                                        bgcolor: (themeObj) => themeObj.palette.grey[900]
                                    }
                                } }) }), _jsxs(Typography, { variant: 'caption', color: 'text.secondary', sx: { whiteSpace: 'nowrap' }, children: [usage, "%"] })] }));
            }
        },
        {
            field: 'cateringPackage',
            headerName: t('events.eventDetails.routeTab.cateringPackage'),
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => {
                const key = CateringPackageEnumDisplay[params.row.cateringPackage];
                return key ? t(key) : '—';
            }
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.5,
            minWidth: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', gap: 1, alignItems: 'center', children: [_jsx(IconButton, { color: 'default', onClick: (event) => {
                            event.stopPropagation();
                            handleOpenDetailDialog(params.row, 'map');
                        }, children: _jsx(Map1, { variant: 'Outline' }) }), !isSfeermeester && (_jsx(IconButton, { color: 'inherit', onClick: (event) => handleOpenRowMenu(event, params.row), disabled: isSfeermeester, children: _jsx(MoreHoriz, {}) }))] }))
        }
    ];
    const handleCreateRoute = async (payload) => {
        if (!eventId)
            return false;
        let success = false;
        const cateringPackageValue = payload.cateringPackage != null ? Number(payload.cateringPackage) : null;
        const isEditing = !!editingRoute;
        let savedRoute;
        try {
            if (editingRoute && editingRouteDetail) {
                savedRoute = await dispatch(updateRouteAsync({
                    id: editingRoute.id,
                    name: payload.routeName,
                    cateringPackage: cateringPackageValue,
                    pickupLocationIds: payload.orderedPickupLocations.map((l) => String(l.value)),
                    vehicleIds: payload.vehicles.map((v) => String(v.value))
                })).unwrap();
                toast.success(t('events.eventDetails.routeTab.routeUpdatedSuccessfully'));
            }
            else if (!editingRoute) {
                savedRoute = await dispatch(createRouteAsync({
                    eventId,
                    name: payload.routeName,
                    cateringPackage: cateringPackageValue,
                    pickupLocationIds: payload.orderedPickupLocations.map((l) => String(l.value)),
                    vehicleIds: payload.vehicles.map((v) => String(v.value))
                })).unwrap();
                toast.success(t('events.eventDetails.routeTab.routeCreatedSuccessfully'));
            }
            // Backend auto-assigns unassigned individuals to newly added vehicles.
            const autoAssignedCount = savedRoute?.autoAssignedCount;
            if (autoAssignedCount != null) {
                if (autoAssignedCount > 0) {
                    toast.success(t('events.eventDetails.routeTab.vehicleAutoAssignSuccess', { count: autoAssignedCount }));
                }
                else {
                    toast.info(t('events.eventDetails.routeTab.vehicleAutoAssignNoEligible'));
                }
            }
            success = true;
        }
        catch (error) {
            toast.error(error?.error ||
                t(isEditing
                    ? 'events.eventDetails.routeTab.routeUpdateFailed'
                    : 'events.eventDetails.routeTab.routeCreationFailed'));
            success = false;
        }
        finally {
            const sort = sortModel[0];
            dispatch(getRoutesByEventIdAsync({
                eventId,
                query: {
                    pageNumber: 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    sortBy: sort?.field,
                    sortOrder: sort?.sort ?? 'desc',
                    ...filters
                }
            }));
            // refresh vehicle route links so the dropdown hides newly assigned vehicles
            dispatch(getEventVehiclesNotPaginatedByEventIdAsync({ eventId }));
        }
        if (success) {
            setEditingRoute(null);
            setEditingRouteDetail(null);
            setPrepopulatedVehicles([]);
            setOpenCreateDialog(false);
        }
        return success;
    };
    if (!hasEnoughPickupLocations) {
        return (_jsxs(Box, { children: [_jsx(Typography, { variant: 'h4', sx: { mb: 1.5 }, children: t('events.eventDetails.routeTab.noRoutesExist') }), _jsx(AppAlert, { severity: 'info', sx: { mb: 2 }, action: _jsx(Button, { variant: 'contained', size: 'small', color: 'info', onClick: onGoToLocationsTab, endIcon: _jsx(Icon, { icon: 'eva:arrow-forward-outline' }), children: t('events.eventDetails.routeTab.view') }), children: _jsx(Typography, { variant: 'body2', component: 'span', children: t('events.eventDetails.routeTab.routesNeedPickupLocationsInfo') }) })] }));
    }
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { columns: columns, rows: filteredRoutes, showHeaderControls: false, showFilters: true, onApplyFilters: (newFilters) => {
                    const mappedFilters = { ...newFilters };
                    if (newFilters.locationCount) {
                        mappedFilters.pickupLocationId = newFilters.locationCount;
                        delete mappedFilters.locationCount;
                    }
                    if (newFilters.vehicleCount) {
                        mappedFilters.vehicleId = newFilters.vehicleCount;
                        delete mappedFilters.vehicleCount;
                    }
                    setFilters(mappedFilters);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, resetFiltersKey: resetFiltersKey, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, filterOpen: gridFilterOpen, onFilterOpenChange: setGridFilterOpen, filterOptions: {
                    locationCount: pickupLocationOptions.map((opt) => ({
                        value: opt.value,
                        label: opt.label
                    })),
                    startLocation: pickupLocationOptions.map((opt) => ({
                        value: opt.value,
                        label: opt.label
                    })),
                    endLocation: pickupLocationOptions.map((opt) => ({ value: opt.value, label: opt.label })),
                    vehicleCount: vehicleOptions.map((opt) => ({ value: opt.value, label: opt.label })),
                    cateringPackage: Object.entries(CateringPackageEnumDisplay).map(([value, key]) => ({
                        value: Number(value),
                        label: t(key)
                    }))
                }, paginationMode: 'server', rowCount: routes?.totalCount || 0, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, showNewHeaderControls: true, noRowsText: t('events.eventDetails.routeTab.noRoutesFound'), maxHeight: 'calc(100dvh - 240px)', customButton: _jsxs(Box, { display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', justifyContent: 'space-between', alignItems: isSmallScreen ? undefined : 'center', gap: isSmallScreen ? 1.5 : 0, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: [_jsx(Typography, { variant: 'h4', children: t('events.eventDetails.routeTab.routesCount', { count: routes?.totalCount || 0 }) }), isSmallScreen && (_jsxs(Box, { display: 'flex', gap: 1, children: [_jsx(Button, { startIcon: _jsx(ExportSquare, {}), variant: 'outlined', size: 'small', color: 'primary', onClick: handleExportRoutes, disabled: isExporting, children: t('actions.export', 'Export') }), !isSfeermeester && (_jsx(Button, { startIcon: _jsx(AddCircle, {}), variant: 'contained', size: 'small', color: 'primary', onClick: () => setOpenCreateDialog(true), disabled: isSfeermeester, children: t('events.eventDetails.routeTab.createRoute') }))] }))] }), _jsxs(Box, { display: 'flex', gap: 1.5, children: [_jsx(SearchFilter, { forceDesktopVariant: true, searchValue: searchValue, onSearchChange: setSearchValue, placeholder: t('events.eventDetails.routeTab.searchRoutes'), onClear: () => setSearchValue(''), onFilterClick: () => setGridFilterOpen(true), showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                        setFilters({});
                                        setResetFiltersKey((prev) => prev + 1);
                                    } }), !isSmallScreen && (_jsxs(_Fragment, { children: [_jsx(Button, { startIcon: _jsx(ExportSquare, {}), variant: 'outlined', size: 'large', color: 'primary', onClick: handleExportRoutes, disabled: isExporting, children: t('actions.export', 'Export') }), !isSfeermeester && (_jsx(Button, { startIcon: _jsx(AddCircle, {}), variant: 'contained', size: 'large', color: 'primary', onClick: () => setOpenCreateDialog(true), disabled: isSfeermeester, children: t('events.eventDetails.routeTab.createRoute') }))] }))] })] }), boxProps: { sx: { display: 'flex', flexDirection: 'column', gap: '12px' } } }), _jsx(CreateRouteDialog, { open: openCreateDialog, onClose: () => {
                    setOpenCreateDialog(false);
                    setEditingRoute(null);
                    setEditingRouteDetail(null);
                    setPrepopulatedVehicles([]);
                }, pickupLocationOptions: pickupLocationOptions, vehicleOptions: availableVehicleOptions, editingRoute: editingRoute
                    ? {
                        id: editingRoute.id,
                        routeName: editingRoute.name,
                        pickupLocations: editingRoute.pickupLocations,
                        vehicles: prepopulatedVehicles,
                        cateringPackage: editingRoute.cateringPackage
                    }
                    : null, onCreate: handleCreateRoute }), _jsx(ConfirmationDialog, { open: deleteConfirmOpen, onClose: () => {
                    setDeleteConfirmOpen(false);
                    setRouteToDelete(null);
                }, title: t('events.eventDetails.routeTab.deleteRoute'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', fullWidth: true, onClick: () => {
                                setDeleteConfirmOpen(false);
                                setRouteToDelete(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', size: 'large', fullWidth: true, onClick: handleConfirmDelete, children: t('actions.deleteNow') })] }), children: _jsx(Box, { p: 2, children: _jsx(Typography, { children: t('events.eventDetails.routeTab.deleteRouteConfirmation', {
                            routeName: routeToDelete?.name
                        }) }) }) }), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleCloseRowMenu, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, actions: rowActions }), _jsx(LocationsListDialog, { open: openLocationsDialog, onClose: () => setOpenLocationsDialog(false), locations: selectedLocationsForDialog }), _jsx(VehiclesListDialog, { open: openVehiclesDialog, onClose: () => setOpenVehiclesDialog(false), routeName: selectedRouteNameForVehicles, rows: selectedVehiclesForDialog }), _jsx(RouteMapDialog, { open: openMapDialog, onClose: () => setOpenMapDialog(false), routeName: selectedRouteNameForMap, pickupLocations: selectedPickupLocationsForMap })] }));
};
export default RoutesContent;
